import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import TurnstileWidget from "@/components/common/TurnstileWidget";
import { shouldEnableTurnstile } from "@/lib/turnstile";

export default function LoginPage() {
  const navigate = useNavigate();
  const { requestLoginCode, verifyLoginCode } = useAuth();
  const registrationEnabled =
    String(process.env.REACT_APP_REGISTRATION_ENABLED || "false").toLowerCase() === "true";
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("credentials");
  const [otpCode, setOtpCode] = useState("");
  const [expiresIn, setExpiresIn] = useState(0);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileSiteKey =
    process.env.REACT_APP_TURNSTILE_SITE_KEY || "0x4AAAAAACgE8YFxQViaDsnD";
  const turnstileEnabled = shouldEnableTurnstile();

  const maskedEmail = (() => {
    const email = String(formData.email || "").trim();
    const [name, domain] = email.split("@");
    if (!name || !domain) return email;
    if (name.length <= 2) return `${name[0] || ""}***@${domain}`;
    return `${name.slice(0, 2)}***${name.slice(-1)}@${domain}`;
  })();

  const canResend = step === "otp" && expiresIn <= 0 && !loading;

  useEffect(() => {
    if (step !== "otp" || expiresIn <= 0) return undefined;
    const timer = window.setInterval(() => {
      setExpiresIn((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [step, expiresIn]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (step === "credentials") {
      if (turnstileEnabled && !turnstileToken) {
        toast.error("Lütfen güvenlik doğrulamasını tamamlayın.");
        setLoading(false);
        return;
      }
      const result = await requestLoginCode(formData.email, formData.password, turnstileToken);
      if (result.success && result.skipVerification) {
        navigate(result.redirectPath || "/panel");
      } else if (result.success) {
        setStep("otp");
        setOtpCode("");
        setExpiresIn(result.expiresIn || 300);
        setTurnstileToken("");
      }
      setLoading(false);
      return;
    }

    const result = await verifyLoginCode(formData.email, otpCode);
    if (result.success) {
      navigate(result.redirectPath || "/panel");
    }
    setLoading(false);
  };

  const handleResendCode = async () => {
    if (!canResend) return;
    setLoading(true);
    const result = await requestLoginCode(formData.email, formData.password);
    if (result.success) {
      setExpiresIn(result.expiresIn || 300);
      setOtpCode("");
    }
    setLoading(false);
  };

  if (step === "otp") {
    return (
      <div className="min-h-screen relative overflow-hidden bg-slate-950" data-testid="login-page">
        <div className="absolute -top-28 -left-24 h-72 w-72 rounded-full bg-sky-500/20 blur-3xl" />
        <div className="absolute -bottom-28 -right-24 h-72 w-72 rounded-full bg-cyan-400/20 blur-3xl" />
        <div className="relative min-h-screen flex items-center justify-center p-6">
          <div className="w-full max-w-xl rounded-3xl border border-white/15 bg-card/95 p-8 sm:p-10 shadow-2xl backdrop-blur">
            <div className="flex items-center justify-center mb-8">
              <img src="/obihub-logo.png" alt="obihub" className="h-10" />
            </div>
            <p className="text-center text-muted-foreground">
              {maskedEmail || "e-posta adresiniz"} adresine gönderilen 6 haneli kodu girin.
            </p>

            <form onSubmit={handleSubmit} className="mt-10">
              <div className="flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={otpCode}
                  onChange={(value) => setOtpCode((value || "").replace(/\D/g, "").slice(0, 6))}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  containerClassName="justify-center gap-2"
                  data-testid="login-otp-input"
                >
                  <InputOTPGroup className="gap-2">
                    <InputOTPSlot index={0} className="h-14 w-12 rounded-xl border text-2xl font-semibold border-border bg-card" />
                    <InputOTPSlot index={1} className="h-14 w-12 rounded-xl border text-2xl font-semibold border-border bg-card" />
                    <InputOTPSlot index={2} className="h-14 w-12 rounded-xl border text-2xl font-semibold border-border bg-card" />
                    <InputOTPSlot index={3} className="h-14 w-12 rounded-xl border text-2xl font-semibold border-border bg-card" />
                    <InputOTPSlot index={4} className="h-14 w-12 rounded-xl border text-2xl font-semibold border-border bg-card" />
                    <InputOTPSlot index={5} className="h-14 w-12 rounded-xl border text-2xl font-semibold border-border bg-card" />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <Button
                type="submit"
                className="mt-8 w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-medium"
                disabled={loading || otpCode.length !== 6}
                data-testid="login-submit-btn"
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Girişi Tamamla
              </Button>
            </form>

            <div className="mt-5 flex items-center justify-between text-sm">
              <button
                type="button"
                className="text-foreground/80 hover:text-foreground disabled:opacity-50"
                disabled={!canResend}
                onClick={handleResendCode}
                data-testid="login-otp-resend-btn"
              >
                Kodu yeniden gönder
              </button>
              <span className="text-muted-foreground">{expiresIn > 0 ? `${expiresIn}s` : "Yeniden gönderilebilir"}</span>
            </div>

            <Button
              type="button"
              variant="ghost"
              className="mt-3 w-full h-10"
              onClick={() => setStep("credentials")}
              disabled={loading}
              data-testid="login-back-btn"
            >
              E-posta ve şifreye geri dön
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-muted/50 lg:bg-transparent relative overflow-hidden" data-testid="login-page">
      <div className="lg:hidden pointer-events-none absolute -top-20 -left-16 h-56 w-56 rounded-full bg-sky-200/60 blur-3xl motion-safe:animate-pulse" />
      <div className="lg:hidden pointer-events-none absolute top-40 -right-16 h-56 w-56 rounded-full bg-cyan-200/50 blur-3xl motion-safe:animate-pulse [animation-delay:700ms]" />
      <div className="lg:hidden pointer-events-none absolute bottom-10 left-1/3 h-48 w-48 rounded-full bg-blue-200/40 blur-3xl motion-safe:animate-pulse [animation-delay:1400ms]" />
      <div className="hidden lg:flex lg:w-[45%] bg-[#0f172a] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1487a3]/20 via-transparent to-[#10b981]/10" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div>
            <img src="/obihub-logo.png" alt="ObiHub" className="h-10" />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-white leading-tight mb-4">
              {""}<br />{""}
            </h2>
            <p className="text-muted-foreground text-sm leading-relaxed max-w-md">
              {""}
            </p>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-white">7+</div>
              <div className="text-[11px] text-muted-foreground">Entegrasyon</div>
            </div>
            <div className="w-px h-8 bg-white/10" />
            <div className="text-center">
              <div className="text-2xl font-bold text-white">%99.9</div>
              <div className="text-[11px] text-muted-foreground">Uptime</div>
            </div>
            <div className="w-px h-8 bg-white/10" />
            <div className="text-center">
              <div className="text-2xl font-bold text-white">500+</div>
              <div className="text-[11px] text-muted-foreground">Magaza</div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-transparent lg:bg-background relative z-10">
        <div className="w-full max-w-sm rounded-2xl border border-border/80 bg-white/90 backdrop-blur-sm shadow-xl p-6 sm:p-8 lg:rounded-none lg:border-0 lg:bg-transparent lg:shadow-none lg:p-0">
          <div className="flex items-center justify-center mb-8 lg:hidden">
            <img src="/obihub-logo.png" alt="ObiHub" className="h-11" />
          </div>

          <div className="mb-8">
            <p className="text-base lg:text-sm text-muted-foreground mt-1">{"Hesab\u0131n\u0131za giri\u015f yap\u0131n"}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm lg:text-xs font-medium">E-posta</Label>
              <Input
                id="email"
                type="email"
                placeholder="ornek@mail.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="h-12 lg:h-10 text-base lg:text-sm"
                data-testid="login-email-input"
                disabled={step === "otp"}
              />
            </div>
            {step === "credentials" ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm lg:text-xs font-medium">{"\u015eifre"}</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder={"\u015eifrenizi girin"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    className="h-12 lg:h-10 text-base lg:text-sm"
                    data-testid="login-password-input"
                  />
                </div>
                {turnstileEnabled ? (
                  <TurnstileWidget
                    siteKey={turnstileSiteKey}
                    onToken={setTurnstileToken}
                    onExpire={() => setTurnstileToken("")}
                    className="pt-1"
                  />
                ) : null}
              </>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="otp-code" className="text-xs font-medium">Doğrulama Kodu</Label>
                <Input
                  id="otp-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  placeholder="6 haneli kod"
                  value={otpCode}
                  onChange={(e) => setOtpCode((e.target.value || "").replace(/\D/g, "").slice(0, 6))}
                  required
                  className="h-10 tracking-[0.2em]"
                  data-testid="login-otp-input"
                />
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <button
                    type="button"
                    className="hover:text-foreground disabled:opacity-50"
                    disabled={!canResend}
                    onClick={handleResendCode}
                    data-testid="login-otp-resend-btn"
                  >
                    Kodu yeniden gönder
                  </button>
                  <span>{expiresIn > 0 ? `${expiresIn}s` : "Tekrar gönderebilirsiniz"}</span>
                </div>
              </div>
            )}
            <Button
              type="submit"
              className="w-full h-12 lg:h-10 bg-[#1487a3] hover:bg-[#1e7ab3] text-white font-semibold rounded-xl lg:rounded-md"
              disabled={loading || (step === "otp" && otpCode.length !== 6)}
              data-testid="login-submit-btn"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Giriş
            </Button>
            {step === "otp" ? (
              <Button
                type="button"
                variant="ghost"
                className="w-full h-9"
                onClick={() => setStep("credentials")}
                disabled={loading}
                data-testid="login-back-btn"
              >
                E-posta ve şifreye geri dön
              </Button>
            ) : null}
          </form>

          {step === "credentials" ? (
            <div className="mt-3 text-right text-sm">
              <Link to="/sifremi-unuttum" className="text-[#1487a3] hover:underline font-medium">
                Şifremi unuttum
              </Link>
            </div>
          ) : null}

          {registrationEnabled ? (
            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">{"Hesab\u0131n\u0131z yok mu? "}</span>
              <Link to="/kayit" className="text-[#1487a3] hover:underline font-medium" data-testid="register-link">
                {"Kay\u0131t Ol"}
              </Link>
            </div>
          ) : null}
          <p className="mt-8 text-center text-xs lg:text-[11px] text-muted-foreground">
            &copy; {new Date().getFullYear()} ObiHub. {"T\u00fcm haklar\u0131 sakl\u0131d\u0131r."}
          </p>
        </div>
      </div>
    </div>
  );
}
